<template>
  <div class="asc-mx-rec-support-info-attachments--wrapper">
    <div class="asc-mx-rec-support-info-attachments--container">
      <template v-if="fileUpload">
        <asc-file-upload-input
          v-model="attachments"
          class="asc-mx-rec-support-info-attachments--input"
          :title="getTitle"
          :accepted-file-properties="getFileTypeProperties"
          :on-upload-file="handleUploadFile"
          :on-remove-file="handleRemoveFile"
          :on-download-file="handleDownloadFile"
          :handle-update-attachment-caption="handleUpdateAttachmentCaption"
          :allow-file-remove="isEditable && !disabled"
          :allow-file-upload="isEditable && !disabled"
          :can-edit-attachment-captions="isEditable"
          :disabled="disabled"
          :viewable="areFilesVisible"
          :upload-error="getAttachmentsUploadError"
          @handleToggleFileVisibility="handleFileVisibility"
        ></asc-file-upload-input>
      </template>

      <template v-else>
        <v-lazy
          v-if="pagePlots.length"
          v-model="isActive"
          :options="{ threshold: 0.5 }"
        >
          <asc-plots-viewer-wrapper
            mini-plot
            mx-rec-plot
            :is-recommendation-tab="isRecommendationTab"
            :page-plots="pagePlots"
            :editable="arePlotsEditable"
            :plots-eyeballs-visible="arePlotsEyeballsVisible"
            :plots-eyeballs-readonly="arePlotsEyeballsReadonly"
            :visible-plots="selections"
            :loading-plot-ids="loadingPlotIds"
            :chart-data="chartData"
            :hide-hidden-tabs="hideHiddenTabs"
            :on-delete-page-plot="handleDeletePagePlot"
            :on-toggle-page-plot-visibility="handleTogglePagePlotVisibility"
            :on-update-page-plot-caption="onUpdatePagePlotCaption"
            :on-expand="onExpand"
            :on-add-tab-to-page-plot="onAddTabToPagePlot"
            :on-remove-tab-from-page-plot="onRemoveTabFromPagePlot"
            :on-update-tab-view-page-plot="onUpdateTabViewPagePlot"
          ></asc-plots-viewer-wrapper>
        </v-lazy>
      </template>
    </div>
  </div>
</template>

<script>
import AscFileUploadInput from '../molecules/AscFileUploadInput.vue'
import AscPlotsViewerWrapper from '../organisms/AscPlotsViewerWrapper.vue'

import {
  AscMxRecPropsMixin,
  AscMxRecPermissionMixin,
} from '@/components/mixins'

import { COLLINS_PHM_TIER_3, COLLINS_PHM_TIER_4 } from '@/utilities/appConsts'

import _cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'AscMxRecSupportInfoAttachmentsWrapper',

  components: {
    AscFileUploadInput,
    AscPlotsViewerWrapper,
  },

  mixins: [AscMxRecPropsMixin, AscMxRecPermissionMixin],

  props: {
    attachmentsData: {
      type: Object,
      default: () => ({
        attachments: [],
        attachmentsCaption: '',
        selectedFiles: [],
        fileTypeProperties: {},
        attachmentUploadError: [],
      }),
    },
    hasCaptionOption: {
      type: Boolean,
      default: true,
    },
    handleUpdatePlotCaption: {
      type: Function,
      default: () => {},
    },
    attachmentsCaption: {
      type: String,
      default: '',
    },
    pagePlots: {
      type: Array,
      default: () => [],
    },
    loadingPlotIds: {
      type: Array,
      default: () => [],
    },
    chartData: {
      type: Array,
      default: () => [],
    },
    arePlotsEditable: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    type: {
      type: Object,
      default: () => {},
    },
    fileUpload: {
      type: Boolean,
      default: false,
    },
    selectionType: {
      type: Object,
      default: () => {},
    },
    selections: {
      type: Array,
      default: () => [],
    },
    handleLoadPlot: {
      type: Function,
      default: () => {},
    },
    selectedAttachmentsCount: {
      type: Function,
      default: () => {},
    },
    hideHiddenTabs: {
      type: Boolean,
      default: true,
    },
    onDeletePagePlot: {
      type: Function,
      default: () => {},
    },
    onTogglePagePlotVisibility: {
      type: Function,
      default: () => {},
    },
    onUpdatePagePlotCaption: {
      type: Function,
      default: () => {},
    },
    onExpand: {
      type: Function,
      default: () => {},
    },
    onAddTabToPagePlot: {
      type: Function,
      default: () => {},
    },
    onRemoveTabFromPagePlot: {
      type: Function,
      default: () => {},
    },
    onUpdateTabViewPagePlot: {
      type: Function,
      default: () => {},
    },
    isRecommendationTab: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isActive: false,
      visiblePlots: [],
      plotPreviewCaption: this.plotsCaption,
      visibilityConfirmationSent: false,
      visibilityReminderSent: false,
    }
  },

  computed: {
    attachments: {
      get() {
        return _cloneDeep(this.attachmentsData.attachments)
      },
    },

    areFilesEyeballsVisible() {
      return (
        this.isEditable &&
        !this.disabled &&
        this.airlineTierTypeId !== COLLINS_PHM_TIER_3
      )
    },

    areFilesVisible() {
      return (
        /*Investigate Tab
          Always Visible

        MX Rec Tab
          Visible if Externally visible or Tier 3
        */
        this.airlineTierTypeId === COLLINS_PHM_TIER_3 ||
        !this.isRecommendationTab
      )
    },

    getFileTypeProperties() {
      return this.attachmentsData.fileTypeProperties
    },

    getAttachmentsCaption() {
      return this.attachmentsData.attachmentsCaption
    },

    getAttachmentsUploadError() {
      return this.attachmentsData.attachmentUploadError
    },

    getTitle() {
      return this.type && this.type.name
        ? this.type.name.charAt(0).toUpperCase() +
            this.type.name.slice(1).toLowerCase()
        : 'Attachments'
    },

    getPrepopulatedPlots() {
      return this.pagePlots.filter(item => item.prepopulated)
    },

    getExternallyVisiblePrepopulatedPlots() {
      return this.getPrepopulatedPlots.filter(
        item => item.isExternallyVisible && item.plotVisible,
      )
    },

    getCustomPlots() {
      return this.pagePlots.filter(item => !item.prepopulated)
    },

    getSelectedRows() {
      const prepopulatedPlots = this.getExternallyVisiblePrepopulatedPlots
      return this.selectedRows + prepopulatedPlots.length
    },

    arePlotsEyeballsVisible() {
      // The isIssueInvestigator prop itself has a condition which includes
      // both check of isIssueInvestigator && !this.isRecommendationTab from mxrec template
      return (
        (this.isEditPermissionInvestigate ||
          this.isReadOnlyPermissionInvestigate) &&
        !this.isRecommendationTab &&
        this.airlineTierTypeId !== COLLINS_PHM_TIER_3 &&
        this.airlineTierTypeId !== COLLINS_PHM_TIER_4
      )
    },

    arePlotsEyeballsReadonly() {
      // For issueReader disable eyeIcon
      return this.isReadOnlyPermissionInvestigate && !this.isRecommendationTab
    },
  },

  watch: {
    selections() {
      this.calculateSelectedRows()
    },
    isRecommendationTab() {
      this.isActive = false
    },
    pagePlots: {
      deep: true,
      handler(pagePlots) {
        this.pagePlots = pagePlots
        if (pagePlots.length) this.calculateSelectedRows()
      },
    },
    chartData(chartData) {
      this.chartData = chartData
    },
  },

  methods: {
    calculateSelectedRows() {
      this.selectedRows = 0
      this.selectedFiles = 0
      // eslint-disable-next-line
      this.attachments.map(fileObj => {
        if (fileObj.isExternallyVisible) {
          this.selectedRows++
          this.selectedFiles++
        }
      })

      const checkIfSelectionsAreInPagePlots = this.selections?.filter(
        selectedId => {
          const isInPagePlots = this.pagePlots.findIndex(
            plot => plot.id === selectedId,
          )
          return isInPagePlots > -1
        },
      )

      this.selectedRows += checkIfSelectionsAreInPagePlots.length
      this.selectedAttachmentsCount(this.selectedRows)
    },

    handleUploadFile(file) {
      this.$emit('onFileUploaded', {
        attachmentTypeId: this.getFileTypeProperties.id,
        fileUpload: file,
        isExternallyVisible: false,
      })
    },

    handleRemoveFile(file) {
      // Pending files will not have an id, but will have a file name passed instead
      if (Number.isInteger(file?.id)) {
        this.$emit('onFileSelected', {
          remove: true,
          id: file.id,
        })
      } else {
        this.$emit('onFileSelected', {
          cancel: true,
          file,
        })
      }
    },

    handleDownloadFile(fileToDownload) {
      this.$emit('onDownloadFile', fileToDownload)
    },

    handleLoadPlotData(plotId) {
      this.handleLoadPlot(plotId)
    },

    handleDeletePagePlot(id) {
      const isPlotVisible = this.visiblePlots.filter(plotId => {
        return plotId === id
      })
      if (isPlotVisible.length) {
        this.selectedRows--
      }
      this.$emit('onRemovePlot', id)
    },

    handleTogglePagePlotVisibility(plotId, plotTypeId, plotVisible) {
      this.handlePlotVisibility(plotId, plotVisible)

      if (this.onTogglePagePlotVisibility) {
        this.onTogglePagePlotVisibility(plotId, plotTypeId, plotVisible)
      }
    },

    handleVisibilityReminders() {
      if (
        this.selectedFiles === 0 &&
        this.visiblePlots.length === 0 &&
        !this.visibilityReminderSent
      ) {
        // flip the state for the toggle reminder so this would only happen the
        // first time the user deselects everything.
        // Emit event to so parent can show toast notification
        this.$emit('onVisibilityReminder', this.title)
        this.visibilityReminderSent = true
      }
      if (
        (this.selectedFiles > 0 || this.visiblePlots.length > 0) &&
        !this.visibilityConfirmationSent
      ) {
        // flip the state for the toggle reminder so this would only happen the
        // first time the user selects something
        // Emit event to so parent can show toast notification
        this.$emit('onVisibilityConfirmation', this.title)
        this.visibilityConfirmationSent = true
      }
    },

    handlePlotVisibility(id, option) {
      const index = this.visiblePlots.findIndex(item => item === id)
      let selected = true
      // only add it to the selected plots if it's not already in there
      if (index === -1 && option) {
        this.visiblePlots.push(id)
        // this.selectedRows++
      } else {
        // this.selectedRows--
        this.visiblePlots = this.visiblePlots.filter(value => {
          return id !== value
        })
        selected = false
      }
      this.$emit('onPlotVisibility', {
        plot: id,
        selected: selected,
      })
      setTimeout(() => {
        this.handleVisibilityReminders()
      }, 300)
    },

    handleUpdateAttachmentCaption(value) {
      this.$emit('onAttachmentCaptionUpdate', value)
    },

    handleFileVisibility(file) {
      if (!file.remove) {
        this.$emit('onFileSelected', file)
      }

      this.$nextTick(() => this.handleVisibilityReminders())
    },
  },
}
</script>

<style lang="scss">
.asc-mx-rec-support-info-attachments--wrapper {
  .asc-mx-rec-support-info-attachments--container {
    margin-bottom: 16px;
    width: 100%;
  }
}
</style>
